import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { MessageSquare, CheckCircle, Clock, Mail } from "lucide-react";

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-2xl font-bold text-[#012d5c] mb-4 pb-2 border-b border-gray-100">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 leading-relaxed">{children}</div>
  </section>
);

const Complaints = () => {
  useEffect(() => {
    document.title = "Complaints | Integro Clinics";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Find out how to raise a concern or complaint with Integro Clinics. We are committed to resolving patient concerns promptly and fairly.";
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <div
        className="bg-[#012d5c] py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(1,45,92,0.88),rgba(1,45,92,0.88)),url('https://images.pexels.com/photos/4031818/pexels-photo-4031818.jpeg?auto=compress&cs=tinysrgb&w=1920&h=900&fit=crop')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <span className="inline-block bg-[#4caf50] text-white text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5">
            Patient Support
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Complaints
          </h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Your voice matters. We take all patient concerns seriously and are committed to resolving them promptly and fairly.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">

            {/* Quick summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
              <div className="bg-[#e0f2fe] rounded-xl p-5 text-center">
                <MessageSquare className="w-7 h-7 text-[#005eb8] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">Raise a Concern</p>
                <p className="text-xs text-gray-600 mt-1">Contact us by email to raise a concern informally.</p>
              </div>
              <div className="bg-[#e8f5e9] rounded-xl p-5 text-center">
                <Clock className="w-7 h-7 text-[#4caf50] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">5-Day Acknowledgement</p>
                <p className="text-xs text-gray-600 mt-1">We aim to acknowledge formal complaints within 5 working days.</p>
              </div>
              <div className="bg-[#f0f4f8] rounded-xl p-5 text-center">
                <CheckCircle className="w-7 h-7 text-[#2ba78c] mx-auto mb-3" />
                <p className="text-sm font-bold text-[#012d5c]">28-Day Response</p>
                <p className="text-xs text-gray-600 mt-1">We aim to provide a full response within 28 days where possible.</p>
              </div>
            </div>

            <Section title="Your Voice Matters">
              <p>
                Integro Clinics values all patient feedback. If you have a concern or complaint about the care or service you have received, we want to hear from you. Raising a concern helps us to improve our services and ensures that your experience is addressed properly.
              </p>
              <p>
                We are committed to handling all concerns with fairness, confidentiality and professionalism. You will not be treated any differently as a result of raising a concern.
              </p>
            </Section>

            <Section title="How to Raise a Concern">
              <p>
                In many cases, concerns can be resolved quickly through informal discussion. If you are unhappy about any aspect of your experience, we encourage you to contact our team directly in the first instance.
              </p>
              <p>
                In the vast majority of cases, a verbal discussion or a brief exchange with a member of our team is sufficient to address the concern. If you would prefer to raise the matter in writing, you are welcome to do so at any stage.
              </p>
            </Section>

            <Section title="Formal Complaints">
              <p>
                If your concern has not been resolved to your satisfaction through informal discussion, or if you prefer to make a formal complaint from the outset, you may do so by submitting a complaint in writing.
              </p>
              <p>
                Formal complaints should be submitted:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "In writing (by email is preferred).",
                  "From your registered email address where possible.",
                  "Addressed to the Compliance Manager.",
                  "To: office@integroclinics.com.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="What to Include">
              <p>To help us investigate your complaint effectively, please include:</p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "Your full name and date of birth.",
                  "Your registered email address.",
                  "A clear description of your concern and the events that led to it.",
                  "The date(s) the events occurred.",
                  "The outcome you are seeking.",
                  "Any relevant supporting information or correspondence.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="What Happens After You Submit a Complaint">
              <p>
                Once we receive your formal complaint:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "We will acknowledge receipt of your complaint within 5 working days.",
                  "Your complaint will be reviewed by the Compliance Manager.",
                  "We will investigate the concern thoroughly and confidentially.",
                  "We will aim to provide a full written response within 28 days. If this is not possible, we will update you on the expected timeline.",
                  "Our response will set out our findings, any actions taken, and any learning identified.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            <Section title="Confidentiality">
              <p>
                All complaints are handled in strict confidence. Your information will only be accessed by those directly involved in investigating and responding to your complaint. We handle all complaint data in accordance with our{" "}
                <a href="/privacy-policy" className="text-[#005eb8] hover:underline">
                  Privacy Policy
                </a>{" "}
                and applicable data protection law.
              </p>
            </Section>

            <Section title="Further Escalation">
              <p>
                If you are not satisfied with our response to your complaint, you may have the right to escalate the matter to an external body, including:
              </p>
              <ul className="list-none space-y-2 mt-2">
                {[
                  "The Care Quality Commission (CQC): the independent regulator of health and social care in England.",
                  "The General Medical Council (GMC): the regulator for doctors, if your concern relates to a clinician's conduct.",
                  "The Parliamentary and Health Service Ombudsman (PHSO): for complaints about NHS and private healthcare providers that have not been resolved.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#4caf50] mt-1 shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </Section>

            {/* Contact card */}
            <div className="bg-[#012d5c] rounded-2xl p-8 text-white">
              <div className="flex items-start gap-4">
                <div className="bg-[#4caf50] rounded-xl p-3 shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold mb-2">Submit a Complaint</h2>
                  <p className="text-blue-200 mb-4 text-sm leading-relaxed">
                    To raise a concern or submit a formal complaint, please email us. Address your email to the Compliance Manager.
                  </p>
                  <a
                    href="mailto:office@integroclinics.com"
                    className="inline-block bg-[#4caf50] hover:bg-[#3d8b40] text-white font-bold px-6 py-3 rounded-lg transition-colors"
                  >
                    office@integroclinics.com
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Complaints;
